import { Link } from "react-router-dom";
import { ArrowLeft, Search, MapPin } from "lucide-react";
import { inr } from "../../lib/api";
import RestaurantCard from "../../components/RestaurantCard";
import VoiceSearch from "../../components/VoiceSearch";
import StockBadge from "../../components/StockBadge";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import { useSearchController } from "./controllers/useSearchController";
import BkSearchView from "./blinkit/BkSearchView";

export default function SearchPage() {
    return useIsBlinkit() ? <BkSearchView /> : <ClassicSearchPage />;
}

function ClassicSearchPage() {
    const {
        tab, setTab, input, setInput, loading, coords, submit, gatedRestaurants, gatedProducts, counts,
    } = useSearchController();

    return (
        <div className="pb-32">
            <header className="px-4 pt-5 pb-3 bg-card sticky top-0 z-10 border-b border-border">
                <div className="flex items-center gap-2">
                    <Link to="/" className="p-1 -ml-1"><ArrowLeft size={22} /></Link>
                    <form onSubmit={submit} className="flex-1 flex items-center bg-muted rounded-xl px-3 py-2.5">
                        <Search size={16} className="text-muted-foreground mr-2" />
                        <input data-testid="search-input" autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search products or shops" className="flex-1 bg-transparent outline-none text-sm" />
                    </form>
                    <VoiceSearch />
                </div>
                {coords && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mt-2 inline-flex items-center gap-1">
                        <MapPin size={10} /> Sorted by distance from you
                    </p>
                )}
                {/* Tabs */}
                <div className="mt-3 flex items-center gap-2 border-b border-border -mx-4 px-4 -mb-3">
                    {[
                        { k: "products", label: "Products" },
                        { k: "shops", label: "Shops" },
                    ].map(({ k, label }) => (
                        <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                            className={`relative pb-2.5 pt-1 text-sm font-bold tracking-wide transition ${tab === k ? "text-primary" : "text-muted-foreground"}`}>
                            <span>{label}</span>
                            <span className={`ml-1.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${tab === k ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{counts[k]}</span>
                            {tab === k && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary rounded-full" />}
                        </button>
                    ))}
                </div>
            </header>

            <div className="p-4">
                {tab === "products" && (
                    <div data-testid="search-products" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                        {gatedProducts.map((p) => (
                            // Every other product card in the app (ProductCard,
                            // BrowseProductsPage, GroupCategoryFeedPage) links to
                            // the dedicated `/product/:id` details route — this
                            // was the only one still pointing at the shop page
                            // instead, so tapping a search hit landed you on the
                            // restaurant's menu rather than the product itself
                            // (user-reported: "Product Details page does not
                            // open"). `/product/:id` is tenant-agnostic (fetches
                            // via `GET /products/{id}` + the standard tenant
                            // header) so this matches the already-working pattern.
                            <Link key={`${p.restaurant_id}-${p.id}`} to={`/product/${p.id}`} data-testid={`prod-hit-${p.id}`}
                                className="bg-card border border-border rounded-2xl p-3 flex gap-3">
                                {(p.images?.[0] || p.restaurant_image) && (
                                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                                        <img src={p.images?.[0] || p.restaurant_image} alt={p.name} className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm line-clamp-1">{p.name}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-1">{p.restaurant_name}</p>
                                    <p className="text-sm font-bold mt-0.5">{inr(p.selling_price)} {p.mrp > p.selling_price && <span className="text-xs text-muted-foreground line-through font-normal">{inr(p.mrp)}</span>}</p>
                                    <div className="mt-1"><StockBadge product={p} /></div>
                                </div>
                                {p._distance_km != null && (
                                    <span className="text-[10px] font-bold text-muted-foreground self-start">{p._distance_km.toFixed(1)} km</span>
                                )}
                            </Link>
                        ))}
                        {!loading && input && gatedProducts.length === 0 && (
                            <p className="text-center text-sm text-muted-foreground py-12">No products match &ldquo;{input}&rdquo;</p>
                        )}
                    </div>
                )}

                {tab === "shops" && (
                    <div data-testid="search-restaurants" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                        {gatedRestaurants.map((r) => <RestaurantCard key={r.id} r={r} compact />)}
                        {!loading && input && gatedRestaurants.length === 0 && (
                            <p className="col-span-2 text-center text-sm text-muted-foreground py-12">No shops match &ldquo;{input}&rdquo;</p>
                        )}
                    </div>
                )}

                {loading && <p className="text-center text-xs text-muted-foreground py-2">Searching…</p>}
            </div>
        </div>
    );
}
